import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { apiClient } from "@api/apiClient";
import type { FacebookPage, FacebookPagesResponse } from "@interfaces/api.interface";

export type { FacebookPage };

export interface FanpageState {
  pages: FacebookPage[];
  loading: boolean;
  error: string | null;
}

const initialState: FanpageState = {
  pages: [],
  loading: false,
  error: null,
};

export const fetchFacebookPages = createAsyncThunk(
  "fanpage/fetchFacebookPages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<FacebookPagesResponse>(
        "/api/facebook/internal/pages"
      );
      return response.data.items;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch Facebook pages"
      );
    }
  }
);

const fanpageSlice = createSlice({
  name: "fanpage",
  initialState,
  reducers: {
    clearPages: (state) => {
      state.pages = [];
      state.error = null;
    },
    updatePageSyncStatus: (
      state,
      action: PayloadAction<{ pageId: string; messageSyncStatus: "NOT_SYNCED" | "IN_PROGRESS" | "SYNCED" }>
    ) => {
      const page = state.pages.find((p) => p.id === action.payload.pageId);
      if (page) {
        page.messageSyncStatus = action.payload.messageSyncStatus;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFacebookPages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchFacebookPages.fulfilled,
        (state, action: PayloadAction<FacebookPage[]>) => {
          state.loading = false;
          state.pages = action.payload;
          state.error = null;
        }
      )
      .addCase(fetchFacebookPages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearPages, updatePageSyncStatus } = fanpageSlice.actions;
export default fanpageSlice.reducer;
